// Solar price maths for the console's Pricing tab (the "What homeowners see"
// worked example). Prices only: generation, savings and roof geometry are
// worked out server-side and never ship in public JS.
//
// MUST give the same answers as the API's solar price function
// (serverless/110-public-api/functions/public/solar_pricing.py), which prices
// the real estimates and leads:
//   price(n)  = basePrice + n × panelPrice + the inverter for kWp(n) (+ battery)
//   inverter  = the first PRICED step, ascending by upToKwp, whose upToKwp
//               covers the system (kWp ≤ upToKwp + 1e-9); unpriced steps skipped
//   null      below minPanels, above the largest priced step, or prices unset
//   range     price ± 10%, each end rounded to the nearest £50
import type { SolarInverter, SolarPricing } from "@widget";

// ─── Constants ───────────────────────────────────────────────────────────────

/** The range shown to homeowners: ± this share of the price. */
export const SOLAR_SPREAD = 0.1;
/** Allow for floating-point kWp (e.g. 10 × 0.435) when comparing with a step. */
const KWP_EPS = 1e-9;

/** Round to the nearest £50 (as every estimate the homeowner sees is). */
export const round50 = (n: number) => Math.round(n / 50) * 50;

// ─── System sizes and inverters ──────────────────────────────────────────────

/** An inverter step with a price (offered to homeowners). */
export type PricedInverter = SolarInverter & { price: number };

/** System size in kWp for n panels. */
export const kwp = (n: number, p: SolarPricing) => (n * p.panelWatts) / 1000;

/** The priced inverter steps, smallest first. */
export function pricedInverters(p: SolarPricing): PricedInverter[] {
  return p.inverters.filter((i): i is PricedInverter => i.price !== null).sort((a, b) => a.upToKwp - b.upToKwp);
}

/** True when a system can be priced: base, per panel and at least one inverter set. */
export function isPriced(p: SolarPricing): boolean {
  return p.basePrice !== null && p.panelPrice !== null && pricedInverters(p).length > 0;
}

/** True when homeowners can add the battery (switched on and priced). */
export const batteryOffered = (p: SolarPricing) => p.battery.enabled && p.battery.price !== null;

/** Most panels the price list covers: what the largest priced inverter step takes. */
export function maxPanelsPriced(p: SolarPricing): number {
  const inv = pricedInverters(p);
  if (!isPriced(p) || !inv.length || p.panelWatts <= 0) return 0;
  return Math.floor((inv[inv.length - 1].upToKwp * 1000) / p.panelWatts + KWP_EPS);
}

/** The inverter for n panels, or null when the system is above the largest priced step. */
export function inverterFor(n: number, p: SolarPricing): PricedInverter | null {
  const size = kwp(n, p);
  return pricedInverters(p).find((i) => size <= i.upToKwp + KWP_EPS) ?? null;
}

// ─── Price ───────────────────────────────────────────────────────────────────

/** Installed price (£) for n panels, plus the battery when chosen; null when it can't be priced. */
export function solarPrice(n: number, battery: boolean, p: SolarPricing): number | null {
  if (!isPriced(p) || n < p.minPanels) return null;
  const inv = inverterFor(n, p);
  if (!inv) return null;
  return (p.basePrice ?? 0) + n * (p.panelPrice ?? 0) + inv.price + (battery ? (p.battery.price ?? 0) : 0);
}

/** The range homeowners see, with the exact price and the inverter it came from. */
export interface SolarPriceRange {
  low: number;
  high: number;
  price: number;
  inverter: PricedInverter;
}

/** solarPrice ± SOLAR_SPREAD, each end rounded to £50; null when n can't be priced. */
export function solarPriceRange(n: number, battery: boolean, p: SolarPricing): SolarPriceRange | null {
  const inverter = inverterFor(n, p);
  const price = solarPrice(n, battery, p);
  if (!inverter || price === null) return null;
  return { low: round50(price * (1 - SOLAR_SPREAD)), high: round50(price * (1 + SOLAR_SPREAD)), price, inverter };
}
